import type { Edl } from './edl.js';
import type { Word } from './types.js';
import { remapEdlTiming } from './speech-cuts.js';

export type StorySource = NonNullable<Edl['sources']>[number];

/** Логические часы для существующих алгоритмов речи. Файлы не склеиваются заранее.
 * Зазор отделяет конец одного исходника от начала другого даже при одинаковых таймкодах. */
export function sourceClock(sources: StorySource[]): Array<StorySource & { offset: number }> {
  let offset = 0;
  return sources.map(source => {
    const item = { ...source, offset };
    offset += source.duration_s + 1;
    return item;
  });
}

export function storyWords(sources: StorySource[]): Word[] {
  return sourceClock(sources).flatMap(s => s.words.map(w => ({ ...w, start: w.start + s.offset, end: w.end + s.offset })));
}

/** Только внутреннее представление. На диск и в API сохраняется EDL 2 с локальным временем. */
export function virtualizeStory(edl: Edl): Edl {
  if (edl.version === 1) return structuredClone(edl);
  const result = structuredClone(edl);
  const clock = sourceClock(edl.sources ?? []);
  const offset = (id?: string): number => {
    const source = clock.find(s => s.asset_id === id);
    if (!source) throw new Error(`Неизвестный основной исходник ${id ?? ''}`);
    return source.offset;
  };
  const shift = <T extends { asset_id?: string; src_in: number; src_out: number }>(s: T): T =>
    ({ ...s, src_in: s.src_in + offset(s.asset_id), src_out: s.src_out + offset(s.asset_id) });
  result.version = 1;
  result.timeline = result.timeline.map(shift);
  if (result.editorial) {
    result.editorial.beats = result.editorial.beats.map(shift);
    result.editorial.accents = result.editorial.accents.map(shift);
    result.editorial.excluded_words = result.editorial.excluded_words?.map(shift);
    result.editorial.pause_after = (result.editorial.source_pauses ?? []).map(p => p.at + offset(p.asset_id));
  }
  for (const c of result.cutaways) if (c.anchor) {
    c.anchor.speech_in += offset(c.anchor.asset_id);
    c.anchor.speech_out += offset(c.anchor.asset_id);
  }
  return result;
}

/** Возвращает локальные времена; отрезок, пересекающий файлы, разбивается на части. */
export function materializeStory(edl: Edl, sources: StorySource[]): Edl {
  const result = structuredClone(edl);
  const clock = sourceClock(sources);
  const split = <T extends { src_in: number; src_out: number }>(part: T) => clock.flatMap(s => {
    const start = Math.max(part.src_in, s.offset);
    const end = Math.min(part.src_out, s.offset + s.duration_s);
    return end - start > 0.001 ? [{ ...part, asset_id: s.asset_id, src_in: +(start - s.offset).toFixed(6), src_out: +(end - s.offset).toFixed(6) }] : [];
  });
  result.version = 2;
  result.sources = sources;
  result.timeline = result.timeline.flatMap(split);
  if (result.editorial) {
    result.editorial.beats = result.editorial.beats.flatMap(split);
    result.editorial.accents = result.editorial.accents.flatMap(split);
    result.editorial.excluded_words = result.editorial.excluded_words?.flatMap(split);
    result.editorial.source_pauses = result.editorial.pause_after.flatMap(at => {
      const s = clock.find(s => at >= s.offset && at <= s.offset + s.duration_s);
      return s ? [{ asset_id: s.asset_id, at: at - s.offset }] : [];
    });
    result.editorial.pause_after = [];
  }
  for (const cut of result.cutaways) if (cut.anchor) {
    const a = cut.anchor;
    const source = clock.find(s => a.speech_in >= s.offset && a.speech_out <= s.offset + s.duration_s);
    if (!source) throw new Error('Врезка пересекает границу основных записей');
    a.asset_id = source.asset_id; a.speech_in -= source.offset; a.speech_out -= source.offset;
  }
  return result;
}

/** Обложка и навигация возвращают источник и конкретное вхождение в таймлайн. */
export function storyPosition(edl: Edl, time: number): { assetId: string; sourceTime: number; segmentIndex: number } {
  let elapsed = 0;
  for (const [segmentIndex, s] of edl.timeline.entries()) {
    const duration = s.src_out - s.src_in;
    if (time < elapsed + duration || segmentIndex === edl.timeline.length - 1) {
      if (!s.asset_id) throw new Error('У сегмента нет исходника');
      return { assetId: s.asset_id, sourceTime: s.src_in + Math.max(0, Math.min(duration - 0.001, time - elapsed)), segmentIndex };
    }
    elapsed += duration;
  }
  throw new Error('Пустой монтаж');
}

export { sourceUsage } from './source-usage.js';

export function restoreStorySource(edl: Edl, assetId: string): Edl {
  if (edl.version !== 2 || !edl.sources?.some(s => s.asset_id === assetId)) throw new Error('Исходник отсутствует в этой версии монтажа');
  const restored = structuredClone(edl);
  restored.timeline = edl.sources.flatMap(source => source.asset_id === assetId
    ? [{ asset_id: assetId, src_in: 0, src_out: source.duration_s, reason: 'Запись возвращена пользователем целиком' }]
    : edl.timeline.filter(s => s.asset_id === source.asset_id));
  const before = virtualizeStory(edl), after = virtualizeStory(restored);
  remapEdlTiming(after, before.timeline, after.timeline);
  after.omissions = after.omissions?.filter(s => s.asset_id !== assetId);
  if (after.editorial) after.editorial.excluded_words = after.editorial.excluded_words?.filter(s => s.asset_id !== assetId);
  return materializeStory(after, edl.sources);
}
